import Cookies from 'js-cookie';

const COOKIE_KEY = 'DEV_ENVIRONMENT';

const DEV_ENVIRONMENTS = process.env.DEV_ENVIRONMENTS;
let startupConfig;

/** Capture once, before the first override; runtime settings win over compiled defaults. */
export const getStartupDevConfiguration = () => {
    if (!startupConfig) {
        const { NODE_ENV, VERSION, DEV_ENVIRONMENTS, ...settings } = {
            ...process.env,
            ...window.FM_CONFIG,
        };
        startupConfig = Object.freeze(settings);
    }
    return startupConfig;
};

export const getStartupDevEnvironment = () =>
    DEV_ENVIRONMENTS.find(({ config }) => config.API_URL === getStartupDevConfiguration().API_URL)
        ?.name || null;

const getRuntimeEnv = () =>
    window?.FM_CONFIG?.ENV || (typeof process !== 'undefined' ? process.env.ENV : undefined);

/** Runtime ENV from FM_CONFIG (deploy) or process.env (local). Never trust build-time alone. */
export const isDevEnvironmentSwitcherEnabled = () => {
    if (process.env.NODE_ENV === 'development') return true;

    const env = getRuntimeEnv();
    return env === 'staging' || env === 'preproduction';
};

/** LOCAL only makes sense when running the app locally (pnpm start). */
export const getAvailableDevEnvironments = () => {
    if (process.env.NODE_ENV !== 'development') return DEV_ENVIRONMENTS;
    return [
        ...DEV_ENVIRONMENTS,
        {
            name: 'LOCAL',
            config: { ...getStartupDevConfiguration(), API_URL: 'http://localhost:8080' },
        },
    ];
};

const browserStorage = () => {
    try {
        return globalThis.localStorage;
    } catch {
        return null;
    }
};

const storedDevEnvironment = () => {
    try {
        return browserStorage()?.getItem(COOKIE_KEY) || '';
    } catch {
        return '';
    }
};

/** This origin's storage wins. The cookie still shares a choice with Web4 when storage is empty. */
const readDevEnvironmentName = () => {
    const names = getAvailableDevEnvironments();
    const known = (name) => names.some((env) => env.name === name);
    const stored = storedDevEnvironment();
    if (known(stored)) return stored;
    const cookie = Cookies.get(COOKIE_KEY);
    return known(cookie) ? cookie : '';
};

export const getDevEnvironmentOverride = () => {
    if (!isDevEnvironmentSwitcherEnabled()) return '';
    return readDevEnvironmentName();
};

export const hasDevEnvironmentOverride = () => Boolean(getDevEnvironmentOverride());

export const loadDevEnvironment = (key) => {
    getStartupDevConfiguration();
    if (!isDevEnvironmentSwitcherEnabled()) return;

    const name = key || getDevEnvironmentOverride();
    const target = getAvailableDevEnvironments().find((item) => item.name === name);
    if (!target) return;
    const { ENV, ...settings } = target.config;

    window.FM_CONFIG = {
        ...(window.FM_CONFIG || {}),
        ...settings,
        // LOCAL changes only the API URL; deployment targets also align custom widgets with B3.
        ...(name !== 'LOCAL' && { CUSTOM_WIDGETS_API_URL: settings.BACKEND3_URL }),
    };
};

/** Selecting the startup target or the active override returns to startup settings. */
export const selectDevEnvironment = (name) => {
    setDevEnvironment(
        name === getStartupDevEnvironment() || name === getDevEnvironmentOverride() ? '' : name,
    );
};

export const setDevEnvironment = (key = '') => {
    const cookieOptions = { path: '/' };

    try {
        if (key) browserStorage()?.setItem(COOKIE_KEY, key);
        else browserStorage()?.removeItem(COOKIE_KEY);
    } catch {
        // Private mode can reject storage. The cookie still applies where the browser allows it.
    }

    if (key) {
        Cookies.set(COOKIE_KEY, key, cookieOptions);
        return;
    }

    Cookies.remove(COOKIE_KEY, cookieOptions);
};
